// ============================================================================
// CancelPhoneOrderModal — PR1 (telefon siparişi iptal akışı)
// ============================================================================
// Kullanım: <PackageOrderModals> içinden render edilir; parent ekran
// `cancelModalOpen` state'ini ve `onConfirmed` callback'ini sağlar.
//
// Davranış:
//  - 5 hazır iptal sebebi (i18n key tabanlı) + "Diğer".
//  - Açıklama (note): tüm sebeplerde opsiyonel; "Diğer" seçildiğinde ZORUNLU
//    (frontend validation, max 500 char).
//  - Onay sırasında "Vazgeç" butonu disable, "İptal Et" buton metni
//    "İptal ediliyor..." olur.
//  - API hatasında snackbar parent'a havale edilir; modal açık kalır ki
//    kullanıcı düzeltebilsin.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';

// ----------------------------------------------------------------------------
// Tipler
// ----------------------------------------------------------------------------

/**
 * Sabit iptal sebebi key'leri. Backend `CancelReason` kolonuna doğrudan
 * INSERT edilir (raporlamada filtreleme bu literal'lar üzerinden yapılır).
 * Yeni sebep eklemek istersen burada ve i18n `cancelPhoneOrder.reasons.*`
 * altına aynı isimle key ekle — exhaustive map sayesinde TS hata verir.
 */
export type CancelReasonKey =
  | 'customerCancelled'
  | 'wrongOrder'
  | 'outOfStock'
  | 'courierUnavailable'
  | 'other';

const REASON_KEYS: ReadonlyArray<CancelReasonKey> = [
  'customerCancelled',
  'wrongOrder',
  'outOfStock',
  'courierUnavailable',
  'other',
] as const;

/** Backend'e gönderilecek payload. Parent submit handler'ı bunu alır. */
export interface CancelPhoneOrderPayload {
  reasonKey: CancelReasonKey;
  /** i18n'de çevrilmiş, raporlamada okunabilir sebep metni. */
  reasonText: string;
  /** Opsiyonel; "other" seçildiyse zorunlu. */
  note?: string;
}

export interface CancelPhoneOrderModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Kullanıcı "İptal Et"e bastığında çağrılır. Promise döndürür:
   *  - Resolve → modal kapanır.
   *  - Reject  → modal açık kalır, hata snackbar'ı parent tarafından gösterilir.
   */
  onConfirm: (payload: CancelPhoneOrderPayload) => Promise<void>;
  /** Başlık ve açıklamada gösterilecek sipariş numarası (shortOrderNo). */
  orderNo: string | number;
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

const NOTE_MAX_LENGTH = 500;

const CancelPhoneOrderModal: React.FC<CancelPhoneOrderModalProps> = ({
  open,
  onClose,
  onConfirm,
  orderNo,
}) => {
  const { t } = useTranslation('salesSystem');

  const [reasonKey, setReasonKey] = useState<CancelReasonKey | ''>('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // Modal her açılışta temiz başlasın.
  useEffect(() => {
    if (open) {
      setReasonKey('');
      setNote('');
      setSubmitError(null);
      setSubmitting(false);
      setTouched(false);
    }
  }, [open]);

  const isOther = reasonKey === 'other';

  const reasonError = useMemo(() => {
    if (!touched) return null;
    if (!reasonKey) return t('orderManagement.cancelPhoneOrder.reasonRequired');
    return null;
  }, [touched, reasonKey, t]);

  const noteError = useMemo(() => {
    if (note.length > NOTE_MAX_LENGTH) {
      return t('orderManagement.cancelPhoneOrder.noteMaxLengthError');
    }
    if (touched && isOther && note.trim().length === 0) {
      return t('orderManagement.cancelPhoneOrder.noteRequiredError');
    }
    return null;
  }, [touched, isOther, note, t]);

  const isFormValid =
    !!reasonKey &&
    note.length <= NOTE_MAX_LENGTH &&
    (!isOther || note.trim().length > 0);

  const handleSubmit = async () => {
    setTouched(true);
    if (!isFormValid || !reasonKey) {
      return;
    }
    setSubmitError(null);
    setSubmitting(true);
    try {
      const reasonText = t(`orderManagement.cancelPhoneOrder.reasons.${reasonKey}`);
      await onConfirm({
        reasonKey,
        reasonText,
        note: note.trim().length > 0 ? note.trim() : undefined,
      });
      // Başarılıysa parent onClose() çağırır; yine de güvende olmak için kapat.
      onClose();
    } catch (err: any) {
      const msg = err?.message || err?.Message || String(err);
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <WarningAmberIcon color="error" />
        <Typography variant="h6" component="span" fontWeight={700}>
          {t('orderManagement.cancelPhoneOrder.title')}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box
          sx={{ mb: 2 }}
          dangerouslySetInnerHTML={{
            __html: t('orderManagement.cancelPhoneOrder.description', { orderNo }),
          }}
        />

        <TextField
          select
          fullWidth
          label={t('orderManagement.cancelPhoneOrder.reasonLabel')}
          value={reasonKey}
          onChange={(e) => {
            setReasonKey(e.target.value as CancelReasonKey);
            setTouched(true);
          }}
          error={!!reasonError}
          helperText={reasonError || ' '}
          disabled={submitting}
          sx={{ mb: 2 }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => {
              if (!selected) {
                return (
                  <span style={{ color: '#9e9e9e' }}>
                    {t('orderManagement.cancelPhoneOrder.reasonPlaceholder')}
                  </span>
                );
              }
              return t(`orderManagement.cancelPhoneOrder.reasons.${selected as CancelReasonKey}`);
            },
          }}
        >
          {REASON_KEYS.map((key) => (
            <MenuItem key={key} value={key}>
              {t(`orderManagement.cancelPhoneOrder.reasons.${key}`)}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          label={t('orderManagement.cancelPhoneOrder.noteLabel')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          required={isOther}
          error={!!noteError}
          helperText={
            noteError ||
            (isOther
              ? t('orderManagement.cancelPhoneOrder.noteRequiredHelper')
              : t('orderManagement.cancelPhoneOrder.noteOptionalHelper'))
          }
          disabled={submitting}
          inputProps={{ maxLength: NOTE_MAX_LENGTH + 50 /* hard cap üzeri girişi göster ki uyarı çıksın */ }}
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('orderManagement.cancelPhoneOrder.snackbar.error', { message: submitError })}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t('orderManagement.cancelPhoneOrder.buttons.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting
            ? t('orderManagement.cancelPhoneOrder.buttons.submitting')
            : t('orderManagement.cancelPhoneOrder.buttons.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelPhoneOrderModal;
